import { useState, useMemo } from "react";
import { X } from "lucide-react";

const FilterSubSection = ({ title, options, selectedValues, onChange, hasSearch = false }) => {
    const [searchTerm, setSearchTerm] = useState('');
    
    const filteredOptions = useMemo(() => {
      if (!hasSearch || !searchTerm.trim()) {
        return options;
      }
      return options.filter(option => 
        option.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );
    }, [options, searchTerm, hasSearch]);
  
    function shouldShowResult(hasSearch, searchTerm) {
      if (!hasSearch) {
        return true; // show if search is disabled
      }
    
      if (hasSearch && searchTerm.length > 0) {
        return true; // show if search enabled and term exists
      } else {
        return false;
      }
    }
  
    // Check if all filtered options are selected
    const areAllFilteredSelected = filteredOptions.length > 0 && 
      filteredOptions.every(option => selectedValues.includes(option));
    
    // Check if some (but not all) filtered options are selected
    const areSomeFilteredSelected = filteredOptions.some(option => selectedValues.includes(option)) && 
      !areAllFilteredSelected;
  
    // Handle select all toggle
    const handleSelectAll = () => {
      if (areAllFilteredSelected) {
        // Deselect all filtered options
        const newSelectedValues = selectedValues.filter(value => !filteredOptions.includes(value));
        onChange(newSelectedValues);
      } else {
        // Select all filtered options
        const optionsToAdd = filteredOptions.filter(option => !selectedValues.includes(option));
        onChange([...selectedValues, ...optionsToAdd]);
      }
    };
  
    return (
      <div className="space-y-2">
        {hasSearch && (
          <div className="relative">
            <input
              type="text"
              placeholder={`Search ${title.toLowerCase()}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-1 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
        
        <div className="max-h-32 overflow-y-auto space-y-1">
          {shouldShowResult(hasSearch, searchTerm) && filteredOptions.length > 0 ? (
            <>
              {/* Select All Checkbox */}
              <label className="flex items-center space-x-1 cursor-pointer hover:bg-blue-50 px-1 py-0.5 rounded text-xs font-medium border-b border-gray-200 mb-1">
                <input
                  type="checkbox"
                  checked={areAllFilteredSelected}
                  ref={(input) => {
                    if (input) input.indeterminate = areSomeFilteredSelected;
                  }}
                  onChange={handleSelectAll}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 scale-75"
                />
                <span className="text-blue-600 leading-tight">
                  Select All ({filteredOptions.length})
                </span>
              </label>
              
              {/* Individual Options */}
              {filteredOptions.map((option) => (
                <label key={option} className="flex items-center space-x-1 cursor-pointer hover:bg-gray-50 px-1 py-0.5 rounded text-xs">
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(option)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onChange([...selectedValues, option]);
                      } else {
                        onChange(selectedValues.filter(v => v !== option));
                      }
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 scale-75"
                  />
                  <span className="text-gray-600 leading-tight">
                    {option.split(" ").map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(" ")}
                  </span>
                </label>
              ))}
            </>
          ) : (
            <div className="text-xs text-gray-500 italic py-1">
              {filteredOptions.length > 0 ? "Try Search Something to get results" : `No ${title.toLowerCase()} found`}
            </div>
          )}
        </div>
      </div>
    );
  };
  export default FilterSubSection;